import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';

import { Dropdown } from 'primereact/dropdown';
import { addExamToGroupOperation } from './operations/add-exam-to-group-operation';
import { PaymentServiceSelector } from '../../../components/payment-service-selector';
import { observer } from 'mobx-react-lite';
import { ExternalExamGroup } from '../../../../../../../api/types/external-exams/external-exam-group';
import { AbstractExternalExam } from '../../../../../../../api/types/settings/studies/abstract-external-exam';
import { Major } from '../../../../../../../api/types/settings/studies/major';
import globalSettingsStores from '../../../../../../../stores/settings-stores';
import { EditNumber } from '../../../../../../../component/ui/edit-text/edit-number';
import CancelButton from '../../../../../../../component/ui/buttons/cancel-button/cancel-button';
import ApproveButtons from '../../../../../../../component/ui/buttons/approve-button/approve-buttons';
import externalExamGroupsStore from '../../../../../../../stores/external-exam-groups-store';

interface AddExamToGroupDialogProps {
  group: ExternalExamGroup;
  visible: boolean;
  onHide: () => void;
  onGroupUpdate?: (updatedGroup: ExternalExamGroup) => void;
}

export const AddExamToGroupDialog: React.FC<AddExamToGroupDialogProps> =
  observer(({ group, visible, onHide, onGroupUpdate }) => {
    const [selectedMajor, setSelectedMajor] = useState<Major | null>(null);
    const [selectedExam, setSelectedExam] =
      useState<AbstractExternalExam | null>(null);
    const [startAt, setStartAt] = useState<Date | null>(null);
    const [durationMinutes, setDurationMinutes] = useState<number>(90);
    const [studentRegistrationDueDate, setStudentRegistrationDueDate] =
      useState<Date | null>(null);
    const [paymentServiceId, setPaymentServiceId] = useState<number | null>(
      null,
    );
    const [paymentServiceVersionId, setPaymentServiceVersionId] = useState<
      number | null
    >(null);
    const [loading, setLoading] = useState(false);

    // Reset state when dialog is closed or opened
    useEffect(() => {
      if (!visible) {
        setSelectedMajor(null);
        setSelectedExam(null);
        setStartAt(null);
        setDurationMinutes(90);
        setStudentRegistrationDueDate(null);
        setPaymentServiceId(null);
        setPaymentServiceVersionId(null);
      }
    }, [visible]);

    // Filter exams by selected major and exclude exams already in the group
    const availableExams = useMemo(() => {
      if (!selectedMajor) {
        return [];
      }
      return globalSettingsStores.externalExams.filter(
        (exam) =>
          exam.majorId === selectedMajor.id &&
          !group.exams.some(
            (groupExam) => groupExam.abstractExternalExamsId === exam.id,
          ),
      );
    }, [selectedMajor, group.exams]);

    const handleSave = async () => {
      if (!selectedExam || !startAt || !studentRegistrationDueDate) {
        return;
      }

      await addExamToGroupOperation(
        group.id,
        {
          abstractExternalExamsId: selectedExam.id,
          startAt,
          durationMinutes,
          studentRegistrationDueDate,
          paymentServiceId: selectedExam.paymentServiceId,
        },
        setLoading,
        () => {
          setSelectedMajor(null);
          setSelectedExam(null);
          setStartAt(null);
          setDurationMinutes(90);
          setStudentRegistrationDueDate(null);
          setPaymentServiceId(null);
          setPaymentServiceVersionId(null);
          onHide();
          // Update the parent component's selectedGroup with the updated group from store
          if (onGroupUpdate) {
            const updatedGroup = externalExamGroupsStore.groups.find(
              (g) => g.id === group.id,
            );
            if (updatedGroup) {
              onGroupUpdate(updatedGroup);
            }
          }
        },
      );
    };

    const isValid =
      selectedMajor &&
      selectedExam &&
      startAt &&
      durationMinutes > 0 &&
      studentRegistrationDueDate;

    return (
      <Dialog
        header={`הוספת מבחן למקבץ: ${group.groupName}`}
        visible={visible}
        onHide={onHide}
        style={{
          width: '600px',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
        }}
        modal
        closable={!loading}
      >
        <div style={{ direction: 'rtl', padding: '8px' }}>
          {/* Step 1: Select Major */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                fontSize: '14px',
                color: '#4b5563',
                marginBottom: '8px',
                display: 'block',
              }}
            >
              בחירת מגמה
            </label>
            <Dropdown
              value={selectedMajor}
              onChange={(e) => {
                setSelectedMajor(e.value);
                setSelectedExam(null); // Reset selected exam when major changes
              }}
              options={globalSettingsStores.majors}
              optionLabel="majorName"
              placeholder="בחירת מגמה"
              filter
              filterBy="majorName"
              style={{
                width: '100%',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
              }}
            />
          </div>

          {/* Step 2: Select Exam (only shown when major is selected) */}
          {selectedMajor && (
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  fontSize: '14px',
                  color: '#4b5563',
                  marginBottom: '8px',
                  display: 'block',
                }}
              >
                בחר מבחן
              </label>
              <Dropdown
                value={selectedExam}
                onChange={(e) => setSelectedExam(e.value)}
                options={availableExams}
                optionLabel="examName"
                placeholder="בחר מבחן"
                style={{
                  width: '100%',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                disabled={availableExams.length === 0}
              />
              {availableExams.length === 0 && (
                <p
                  style={{
                    color: '#dc2626',
                    fontSize: '14px',
                    marginTop: '8px',
                  }}
                >
                  כל המבחנים של מגמה זו כבר קיימים במקבץ זה
                </p>
              )}
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                fontSize: '14px',
                color: '#4b5563',
                marginBottom: '8px',
                display: 'block',
              }}
            >
              תאריך ושעת התחלה
            </label>
            <Calendar
              value={startAt}
              onChange={(e) => setStartAt(e.value as Date)}
              showTime
              hourFormat="24"
              dateFormat="dd/mm/yy"
              style={{ width: '100%' }}
              inputStyle={{ direction: 'rtl', textAlign: 'right' }}
              panelStyle={{ direction: 'ltr' }}
            />
          </div>

          <EditNumber
            title="משך הבחינה (דקות)"
            value={durationMinutes}
            onChange={setDurationMinutes}
            min={1}
          />

          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                fontSize: '14px',
                color: '#4b5563',
                marginBottom: '8px',
                display: 'block',
              }}
            >
              תאריך אחרון לרישום
            </label>
            <Calendar
              value={studentRegistrationDueDate}
              onChange={(e) => setStudentRegistrationDueDate(e.value as Date)}
              dateFormat="dd/mm/yy"
              style={{ width: '100%' }}
              inputStyle={{ direction: 'rtl', textAlign: 'right' }}
              panelStyle={{ direction: 'ltr' }}
            />
          </div>

          {/*          <PaymentServiceSelector
            selectedPaymentServiceId={paymentServiceId}
            onPaymentServiceChange={(id, versionId) => {
              setPaymentServiceId(id);
              setPaymentServiceVersionId(versionId);
            }}
          />*/}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
            }}
          >
            <CancelButton onClick={onHide} disabled={loading} />
            <ApproveButtons
              onClick={handleSave}
              isLoading={loading}
              disabled={!isValid}
            />
          </div>
        </div>
      </Dialog>
    );
  });
